import React, { useState } from "react";
import * as inputTypes from "components/Forms/InputTypes";
import PropTypes from "prop-types";
import TextInputbox from "components/Forms/Elements/Textbox";
import ErrorLabel from "components/Forms/Elements/ErrorLabel";
import AutoSuggest from "components/Forms/Elements/AutoSuggest";
import regexList from "./regex";
import Translate from "helpers/Translate";

function TextboxContainer({
  isFirstItem,
  isLastItem,
  inputs,
  type = inputTypes.TEXTBOX,
  properties,
  errors = {},
  onMoveUpClick,
  onMoveDownClick,
  onTrashClick,
  onInputChange
}) {
  const [showRegex, setShowRegex] = useState(properties.has_regex || false);
  const [renamingOrder, setRenamingOrder] = useState(
    properties.renaming_file_order || ""
  );

  const placeholder = type => {
    switch (type) {
      case inputTypes.TEXTBOX:
        return Translate({ id: "firstname" });
      case inputTypes.EMAIL:
        return Translate({ id: "email" });
      case inputTypes.NUMERIC:
        return Translate({ id: "retirement_age" });
      case inputTypes.TEXTAREA:
        return Translate({ id: "billing_address" });
      case inputTypes.DATESOLO:
        return Translate({ id: "date_of_birth" });
      case inputTypes.DATERANGE:
        return Translate({ id: "invoice_date_range" });
      default:
        return "";
    }
  };

  return (
    <div className="box mb-20">
      <div className="title my-10">
        {Translate({ id: "input_type" })}:{" "}
        <span className="value">{type.toUpperCase()}</span>
        <div className="mini-toolbar fr">
          {!isFirstItem > 0 && (
            <i
              title="Move Up"
              className="fas fa-caret-up move mr-10"
              onClick={() => onMoveUpClick(properties.uuid_container)}
            ></i>
          )}
          {!isLastItem && (
            <i
              title={Translate({ id: "move_down" })}
              className="fas fa-caret-down move mr-10"
              onClick={() => onMoveDownClick(properties.uuid_container)}
            ></i>
          )}
          <i
            title={Translate({ id: "remove" })}
            className="fas fa-trash-alt trash"
            onClick={() => onTrashClick(properties)}
          ></i>
        </div>
      </div>
      {/* Mandatory Checkbox */}
      <div className="form-input-vgroup my-10">
        <label
          htmlFor={`mandatory-${properties.uuid_container}`}
          className="input-item"
        >
          <input
            type="checkbox"
            id={`mandatory-${properties.uuid_container}`}
            className="form-input"
            checked={properties.mandatory}
            onChange={e =>
              onInputChange({
                uuid_container: properties.uuid_container,
                mandatory: e.target.checked
              })
            }
          />
          {Translate({ id: "mandatory" })}
        </label>
      </div>

      {/* Rename Checkbox */}
      <div className="form-input-vgroup my-10">
        <label
          htmlFor={`rename-${properties.uuid_container}`}
          className="input-item"
        >
          <input
            type="checkbox"
            id={`rename-${properties.uuid_container}`}
            className="form-input"
            checked={properties.rename || false}
            onChange={e =>
              onInputChange({
                uuid_container: properties.uuid_container,
                rename: e.target.checked
              })
            }
          />

          {Translate({ id: "use_in_renaming_file" })}
        </label>
        {properties.rename && (
          <select
            id={`rename-order${properties.uuid_container}`}
            className="form-input mr-20"
            value={renamingOrder}
            onChange={e => {
              setRenamingOrder(e.target.value);
              onInputChange({
                uuid_container: properties.uuid_container,
                renaming_file_order: e.target.value
              });
            }}
          >
            <option>Select Renaming Order</option>
            {inputs.map((inp, index) => (
              <option key={index}>{index + 1}</option>
            ))}
          </select>
        )}
      </div>

      {/* Regex */}
      {type === inputTypes.TEXTBOX ? (
        <div className="form-input-vgroup my-10">
          <label
            htmlFor={`has_regex-${properties.uuid_container}`}
            className="input-item"
          >
            <input
              type="checkbox"
              id={`has_regex-${properties.uuid_container}`}
              className="form-input"
              checked={properties.has_regex || false}
              onChange={e => {
                setShowRegex(e.target.checked);
                onInputChange({
                  uuid_container: properties.uuid_container,
                  has_regex: e.target.checked
                });
              }}
            />
            {Translate({ id: "regex_validation" })}
          </label>
          <ErrorLabel
            errors={errors}
            errorKey={`fields.${properties.key}.has_regex`}
          />
          {/* DropDown to enter or select the regex */}
          {showRegex ? (
            <>
              <label htmlFor={`regex-${properties.uuid_container}`}>
                {Translate({ id: "regex_value" })}
              </label>
              <AutoSuggest
                data={regexList}
                displayItemInList="label"
                displayItemInTextbox="value"
                keyword={keyword =>
                  onInputChange({
                    uuid_container: properties.uuid_container,
                    regex: keyword
                  })
                }
                onClose={() => {
                  onInputChange({
                    uuid_container: properties.uuid_container,
                    regex: ""
                  });
                }}
                onSelect={item => {
                  onInputChange({
                    uuid_container: properties.uuid_container,
                    regex: item.value
                  });
                }}
                placeholder={Translate({
                  id: "enter_a_new_regex_or_select_from_the_list"
                })}
                fieldValue={properties.regex}
                errors={errors}
                errorKey={`fields.${properties.key}.regex`}
              />
            </>
          ) : null}
        </div>
      ) : null}

      {/* Show child of which parent */}
      {properties.parent && (
        <div className="mb-10">
          <label htmlFor={`child-${properties.uuid_container}`}>
            {Translate({ id: "child_of" })}:
          </label>
          {properties.parent.value}
        </div>
      )}

      <div className="mb-10">
        <label htmlFor={`name-${properties.uuid_container}`}>
          {Translate({ id: "unique_field_name" })}
        </label>
        <TextInputbox
          type="text"
          id={`name-${properties.uuid_container}`}
          placeholder={placeholder(type)}
          name="name"
          errorKey={`fields.${properties.key}.name`}
          fieldValue={properties.name}
          onReturnKey={() => {}}
          errors={errors}
          formErrorClass="form-error-inverse"
          onChange={e =>
            onInputChange({
              uuid_container: properties.uuid_container,
              name: e.target.value
            })
          }
          onBlur={e =>
            onInputChange({
              uuid_container: properties.uuid_container,
              name: e.target.value.replace(/\s/g, "")
            })
          }
        />
        <div className="help mt-5">
          {Translate({ id: "name_that_uniquely_identifies_this_field" })}
        </div>
      </div>
      <label htmlFor={`label-${properties.uuid_container}`}>
        {Translate({ id: "label_text" })}
      </label>
      <TextInputbox
        type="text"
        id={`label-${properties.uuid_container}`}
        placeholder={placeholder(type)}
        name="label"
        errorKey={`fields.${properties.key}.label`}
        fieldValue={properties.label}
        onReturnKey={() => {}}
        errors={errors}
        formErrorClass="form-error-inverse"
        onChange={e =>
          onInputChange({
            uuid_container: properties.uuid_container,
            label: e.target.value
          })
        }
      />

      <label htmlFor={`label-${properties.uuid_container}`}>sample value</label>
      <TextInputbox
        type="text"
        id={`label-${properties.uuid_container}`}
        placeholder={placeholder(type)}
        name="label"
        errorKey={`fields.${properties.key}.label`}
        fieldValue={properties.value}
        onReturnKey={() => {}}
        errors={errors}
        formErrorClass="form-error-inverse"
        onChange={e =>
          onInputChange({
            uuid_container: properties.uuid_container,
            value: e.target.value
          })
        }
      />
    </div>
  );
}

TextboxContainer.propTypes = {
  isFirstItem: PropTypes.bool.isRequired,
  isLastItem: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  properties: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onTrashClick: PropTypes.func.isRequired,
  onMoveUpClick: PropTypes.func.isRequired,
  onMoveDownClick: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired
};

export default TextboxContainer;
